import { Exercise } from "../components/interfaces/Exercise";

/**
 * Export d'un Exercise al format Moodle Question XML (importable al banc de
 * preguntes de Moodle). Funció pura: Exercise -> string XML.
 *
 * El format s'ha calcat dels exemples reals del client (un <question> per
 * pregunta independent; un sol <question type="cloze"> per als Cloze, amb els
 * buits incrustats a la sintaxi {grau:TIPUS:=correcta~distractor}).
 *
 * Veure taskgen-srv/docs/Disseny-DissenyadorExercicis.md §6.1.
 */

const xmlEscape = (s: string) =>
    (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Per a text que pot contenir HTML/caràcters especials (enunciats).
const cdata = (s: string) => '<![CDATA[' + (s || '') + ']]>';

// Converteix salts de línia de text pla a <br> per a questiontext HTML.
const toHtml = (s: string) => (s || '').replace(/\r?\n/g, '<br>');

const num = (n: number | undefined, fallback: number) => {
    const v = (n === undefined || n === null || isNaN(n as number)) ? fallback : n;
    return Number.isInteger(v) ? String(v) : String(v);
};

// Fracció de Moodle: enters nets ("100", "0", "50") o 5 decimals ("33.33333").
const fraction = (value: number) =>
    Number.isInteger(value) ? String(value) : value.toFixed(5);

const gradeOf = (q: any, ex: Exercise) => num(q?.defaultgrade, ex.defaultgrade ?? 1);
const penaltyOf = (q: any, ex: Exercise) => num(q?.penalty, ex.penalty ?? 0.3333);

// Capçalera comuna d'un <question> (name + questiontext + generalfeedback).
const head = (name: string, statement: string) =>
`    <name>
      <text>${ xmlEscape(name) }</text>
    </name>
    <questiontext format="html">
      <text>${ cdata(toHtml(statement)) }</text>
    </questiontext>
    <generalfeedback format="html">
      <text></text>
    </generalfeedback>`;

const answerEl = (text: string, frac: string, format = 'moodle_auto_format') =>
`    <answer fraction="${ frac }" format="${ format }">
      <text>${ xmlEscape(text) }</text>
      <feedback format="html">
        <text></text>
      </feedback>
    </answer>`;

// ── Preguntes independents ──

function questionXml(q: any, ex: Exercise, index: number): string {
    const name = (ex.name || 'Exercise') + ' ' + (index + 1);
    const grade = gradeOf(q, ex);
    const penalty = penaltyOf(q, ex);

    switch (q.type) {
        case 'shortanswer': {
            const answers = (q.answers || []).filter((a: any) => (a.text || '').trim() !== '');
            const ans = answers.map((a: any) => answerEl(a.text, fraction((a.weight ?? 1) * 100))).join('\n');
            return `  <question type="shortanswer">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>${ penalty }</penalty>
    <hidden>0</hidden>
    <usecase>${ q.caseSensitive ? 1 : 0 }</usecase>
${ ans }
  </question>`;
        }

        case 'truefalse':
            return `  <question type="truefalse">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>${ penalty }</penalty>
    <hidden>0</hidden>
${ answerEl('true', q.correct ? '100' : '0') }
${ answerEl('false', q.correct ? '0' : '100') }
  </question>`;

        case 'multichoice': {
            const options = (q.options || []).filter((o: any) => (o.text || '').trim() !== '');
            const numCorrect = options.filter((o: any) => o.correct).length || 1;
            const single = !q.multiple;
            const ans = options.map((o: any) => {
                const frac = o.correct ? fraction(single ? 100 : 100 / numCorrect) : '0';
                return answerEl(o.text, frac, 'html');
            }).join('\n');
            return `  <question type="multichoice">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>${ penalty }</penalty>
    <hidden>0</hidden>
    <single>${ single ? 'true' : 'false' }</single>
    <shuffleanswers>true</shuffleanswers>
    <answernumbering>abc</answernumbering>
    <showstandardinstruction>0</showstandardinstruction>
    <correctfeedback format="html"><text></text></correctfeedback>
    <partiallycorrectfeedback format="html"><text></text></partiallycorrectfeedback>
    <incorrectfeedback format="html"><text></text></incorrectfeedback>
    <shownumcorrect/>
${ ans }
  </question>`;
        }

        case 'matching': {
            const pairs = (q.pairs || []).filter((p: any) => (p.left || '').trim() !== '' || (p.right || '').trim() !== '');
            const subs = pairs.map((p: any) =>
`    <subquestion format="html">
      <text>${ cdata(toHtml(p.left)) }</text>
      <answer>
        <text>${ xmlEscape(p.right) }</text>
      </answer>
    </subquestion>`).join('\n');
            return `  <question type="matching">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>${ penalty }</penalty>
    <hidden>0</hidden>
    <shuffleanswers>true</shuffleanswers>
    <correctfeedback format="html"><text></text></correctfeedback>
    <partiallycorrectfeedback format="html"><text></text></partiallycorrectfeedback>
    <incorrectfeedback format="html"><text></text></incorrectfeedback>
${ subs }
  </question>`;
        }

        case 'ordering': {
            const items = (q.items || []).filter((it: string) => (it || '').trim() !== '');
            // fraction = posició correcta (1-based), com als exemples del client.
            const ans = items.map((it: string, i: number) =>
`    <answer fraction="${ i + 1 }" format="moodle_auto_format">
      <text>${ xmlEscape(it) }</text>
    </answer>`).join('\n');
            return `  <question type="ordering">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>${ penalty }</penalty>
    <hidden>0</hidden>
    <layouttype>VERTICAL</layouttype>
    <selecttype>ALL</selecttype>
    <selectcount>${ items.length }</selectcount>
    <gradingtype>ABSOLUTE_POSITION</gradingtype>
    <showgrading>SHOW</showgrading>
    <numberingstyle>none</numberingstyle>
    <correctfeedback format="moodle_auto_format"><text></text></correctfeedback>
    <partiallycorrectfeedback format="moodle_auto_format"><text></text></partiallycorrectfeedback>
    <incorrectfeedback format="moodle_auto_format"><text></text></incorrectfeedback>
    <shownumcorrect>0</shownumcorrect>
${ ans }
  </question>`;
        }

        case 'essay':
        default:
            return `  <question type="essay">
${ head(name, q.statement) }
    <defaultgrade>${ grade }</defaultgrade>
    <penalty>0</penalty>
    <hidden>0</hidden>
    <responseformat>editor</responseformat>
    <responserequired>1</responserequired>
    <responsefieldlines>15</responsefieldlines>
    <attachments>0</attachments>
    <attachmentsrequired>0</attachmentsrequired>
    <graderinfo format="html"><text></text></graderinfo>
    <responsetemplate format="html"><text></text></responsetemplate>
  </question>`;
    }
}

// ── Cloze ──

// Escapa els caràcters especials de la sintaxi Cloze dins el text d'una resposta.
const clozeEscape = (s: string) => (s || '').replace(/([\\{}#~/=])/g, '\\$1');

function clozeGapSyntax(gap: any): string {
    const grade = 1;
    if (gap.subtype === 'multichoice') {
        const opts = (gap.options || [])
            .filter((o: any) => (o.text || '').trim() !== '')
            .map((o: any) => (o.correct ? '=' : '') + clozeEscape(o.text));
        return `{${grade}:MULTICHOICE:${ opts.join('~') }}`;
    }
    if (gap.subtype === 'truefalse') {
        const tf = gap.correct ? ['=True', 'False'] : ['=False', 'True'];
        return `{${grade}:MULTICHOICE:${ tf.join('~') }}`;
    }
    // gapfill (SHORTANSWER): resposta + alternatives, totes correctes (=).
    const answers = [gap.answer, ...((gap.alternatives) || [])]
        .filter((a: any) => (a || '').trim() !== '')
        .map((a: string) => '=' + clozeEscape(a));
    return `{${grade}:SHORTANSWER:${ answers.join('~') }}`;
}

function clozeXml(ex: Exercise): string {
    const payload: any = ex.payload || {};
    const gaps: any[] = payload.gaps || [];
    const byId: Record<string, any> = {};
    gaps.forEach(g => { byId[g.id] = g; });

    // Substitueix els marcadors [[N]] del text per la sintaxi Cloze del buit.
    const body = (payload.text || '').replace(/\[\[(\d+)\]\]/g, (_m: string, id: string) =>
        byId[id] ? clozeGapSyntax(byId[id]) : '');

    return `  <question type="cloze">
    <name>
      <text>${ xmlEscape(ex.name || 'Cloze') }</text>
    </name>
    <questiontext format="html">
      <text>${ cdata(toHtml(body)) }</text>
    </questiontext>
    <generalfeedback format="html">
      <text></text>
    </generalfeedback>
    <penalty>${ num(ex.penalty, 0.3333) }</penalty>
    <hidden>0</hidden>
  </question>`;
}

// ── Punt d'entrada ──

export function exerciseToMoodleXml(ex: Exercise): string {
    let questions: string;
    if (ex.type === 'cloze') {
        questions = clozeXml(ex);
    } else {
        const list: any[] = (ex.payload as any)?.questions || [];
        questions = list.map((q, i) => questionXml(q, ex, i)).join('\n\n');
    }
    return `<?xml version="1.0" encoding="UTF-8"?>
<quiz>
${ questions }
</quiz>
`;
}

// Nom d'arxiu suggerit per a la descàrrega.
export function moodleXmlFilename(ex: Exercise): string {
    const base = (ex.name || 'exercise').trim().replace(/[^\w.-]+/g, '_') || 'exercise';
    return base + '.xml';
}
